import { useCallback } from 'react';
import { useToast } from '@hungryroot/components-toast';
import { AddToCookbookToast } from './AddToCookbookToast';
import { FavoriteToast } from './FavoriteToast';
import {
  useManageCookbook,
  type ManageCookbookItem,
} from './ManageCookbookProvider';
import {
  mySavesToastProps,
  useRenameMySavesToast,
} from './useRenameMySavesToast';
import { useShowFavoriteHeart } from './useShowFavoriteHeart';

// Only one favorite toast is shown at a time, but it can be triggered from many
// surfaces (each mounts its own hook instance). Track the current toast id at
// module scope so any surface can remove the previous one before showing a new.
let currentFavoriteToastId: string | null = null;
let favoriteToastSeq = 0;

const FAVORITE_TOAST_DURATION_MS = 3000;

/**
 * Returns a function that shows the favorite/unfavorite toast — but only for
 * Exp 242 (popsicle) variant 1, matching where the heart icon shows.
 * The caller passes the resulting favorited state and an `onUndo` that performs
 * the inverse action (which re-fires this toast, so Undo is chainable).
 *
 * Favoriting another item removes the previous toast instantly (no leave
 * animation) and shows a fresh one, which runs its own enter animation.
 *
 * With customer cookbooks on, a *save* shows the cookbook toast instead —
 * naming the default cookbook and offering **Manage** — so every surface with a
 * heart routes saves, not just the feed. `item` is what makes that possible;
 * omit it and the surface keeps the plain toast even behind the flag, because
 * the sheet needs something to route.
 *
 * A removal always keeps Undo: there is nothing to route once the item is gone.
 */
export function useFavoriteToast() {
  const heart = useShowFavoriteHeart();
  const renameMySaves = useRenameMySavesToast();
  const manageCookbook = useManageCookbook();
  const { toast, remove, dismiss } = useToast();
  const isEnabled = heart.isReady && heart.showHeart;

  return useCallback(
    (props: {
      isFavorited: boolean;
      onUndo: () => void;
      /** The saved item, so the toast can offer to route it. */
      item?: ManageCookbookItem;
    }) => {
      if (!isEnabled) {
        return;
      }

      // Remove the previous favorite toast instantly (no leave animation) so
      // the new one animates in fresh.
      if (currentFavoriteToastId !== null) {
        remove(currentFavoriteToastId);
      }

      // Use an explicit, unique id so the new toast mounts and runs its enter
      // animation (rather than updating the previous in place), and so the
      // toast can dismiss itself on tap.
      const id = `favorite-toast-${++favoriteToastSeq}`;
      currentFavoriteToastId = id;

      // Pass a JSX element, not a render function: this app's ToastContainer
      // renders the message directly as a ReactNode (it does not call it).
      // The caller's onUndo is silent (shows no new toast); FavoriteToast
      // dismisses the toast on undo itself.
      const item = props.item;
      const canManage =
        manageCookbook !== null && props.isFavorited && item !== undefined;
      const onDismiss = () => dismiss(id);
      const toastCopy = mySavesToastProps({
        renameMySaves,
        isInCookbook: props.isFavorited,
        cookbookName: canManage
          ? manageCookbook.defaultCookbookName
          : undefined,
      });

      toast(
        canManage ? (
          <AddToCookbookToast
            isInCookbook
            cookbookName={toastCopy.cookbookName}
            copy={toastCopy.copy}
            action={{
              type: 'manage',
              onManage: () => manageCookbook.openManageForItem(item),
            }}
            onDismiss={onDismiss}
          />
        ) : (
          <FavoriteToast
            isFavorited={props.isFavorited}
            copy={toastCopy.copy}
            onUndo={props.onUndo}
            onDismiss={onDismiss}
          />
        ),
        { id, duration: FAVORITE_TOAST_DURATION_MS }
      );
    },
    [isEnabled, renameMySaves, manageCookbook, toast, remove, dismiss]
  );
}
